
import { useParams } from "react-router-dom";
import { useTestResults } from "@/hooks/useTestResults";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Play } from "lucide-react";
import { TestResult } from "@/hooks/useTestResults";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface TestResultsListProps {
  childId: string;
}

// Helper function to get sort order for grade levels
const getGradeLevelSortOrder = (gradeLevel: string): number => {
  if (!gradeLevel) return 100; // Handle undefined/null case
  const lowercaseGrade = gradeLevel.toLowerCase();
  if (lowercaseGrade === 'infant') return -1;
  const match = lowercaseGrade.match(/\d+/);
  return match ? parseInt(match[0]) : 100;
};

// Define category order to match Testing Modules page
const CATEGORY_ORDER = [
  "Motor Skills",
  "Language Skills",
  "Cognitive Development",
  "Independent Skills",
  "Social and Emotional Skills"
];

// Helper function to get category sort order
const getCategorySortOrder = (category: string): number => {
  const index = CATEGORY_ORDER.indexOf(category);
  return index === -1 ? CATEGORY_ORDER.length : index;
};

// Helper function to group test results
const groupTestResults = (results: TestResult[]) => {
  // First, get the most recent attempt for each test
  const latestResults = results.reduce((acc, result) => {
    const existingResult = acc[result.test_id];
    if (!existingResult || new Date(result.created_at) > new Date(existingResult.created_at)) {
      acc[result.test_id] = result;
    }
    return acc;
  }, {} as Record<string, TestResult>);

  // Then group by grade level and category
  const grouped = Object.values(latestResults).reduce((acc, result) => {
    const gradeLevel = result.tests.grade_level;
    const category = result.tests.category;
    
    if (!acc[gradeLevel]) {
      acc[gradeLevel] = {};
    }
    if (!acc[gradeLevel][category]) {
      acc[gradeLevel][category] = [];
    }
    acc[gradeLevel][category].push(result);
    return acc;
  }, {} as Record<string, Record<string, TestResult[]>>);

  // Sort by grade level
  return Object.entries(grouped)
    .sort(([gradeA], [gradeB]) => {
      return getGradeLevelSortOrder(gradeA) - getGradeLevelSortOrder(gradeB);
    })
    .map(([gradeLevel, categories]) => {
      // Sort categories according to CATEGORY_ORDER
      const sortedCategories = Object.entries(categories)
        .sort(([catA], [catB]) => {
          return getCategorySortOrder(catA) - getCategorySortOrder(catB);
        })
        .map(([category, results]) => {
          // Sort results by date (most recent first)
          const sortedResults = [...results].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          return [category, sortedResults] as [string, TestResult[]];
        });
      
      return [gradeLevel, Object.fromEntries(sortedCategories)] as [string, Record<string, TestResult[]>];
    });
};

export const TestResultsList = ({ childId }: TestResultsListProps) => {
  const { data: testResults, isLoading } = useTestResults(childId);
  const navigate = useNavigate();

  const handleRetakeTest = (testId: string) => {
    localStorage.setItem("selectedChildId", childId);
    navigate(`/test/${testId}`, {
      replace: true,
      state: { reset: true }
    });
  };

  if (isLoading) {
    return <p className="text-gray-600">Loading test results...</p>;
  }

  if (!testResults?.length) {
    return <p className="text-gray-600">No test results available</p>;
  }

  const groupedResults = groupTestResults(testResults);

  return (
    <Accordion type="single" collapsible className="w-full space-y-4">
      {groupedResults.map(([gradeLevel, categories], gradeIndex) => (
        <AccordionItem
          key={gradeLevel}
          value={`grade-${gradeIndex}`}
          className="border rounded-lg px-4"
        >
          <AccordionTrigger className="hover:no-underline">
            <h3 className="text-lg font-semibold">{gradeLevel}</h3>
          </AccordionTrigger>
          <AccordionContent>
            <Accordion type="single" collapsible className="w-full">
              {Object.entries(categories).map(([category, results], categoryIndex) => (
                <AccordionItem
                  key={category}
                  value={`category-${gradeIndex}-${categoryIndex}`}
                  className="border-b-0"
                >
                  <AccordionTrigger className="hover:no-underline py-2">
                    <h4 className="text-md font-medium text-muted-foreground">
                      {category}
                    </h4>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 py-2">
                      {results.map((result) => (
                        <Card key={result.id}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                              <div className="space-y-2">
                                <div>
                                  <h5 className="font-semibold text-primary">
                                    {result.tests.title}
                                  </h5>
                                </div>
                                <div>
                                  <span className="text-lg font-semibold">
                                    {result.score}%
                                  </span>
                                  <span className="text-sm text-muted-foreground ml-2 block sm:inline">
                                    Completed on{" "}
                                    {new Date(result.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full sm:w-auto"
                                onClick={() => handleRetakeTest(result.test_id)}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Retake Test
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
